/*
 * @Description:
 * @Author: Luke Z
 * @Date: 2021-06-22 14:31:25
 * @LastEditors: Jack Deng
 * @LastEditTime: 2021-07-07 16:19:12
 * @FilePath: /unifyxp-v2-store-portal/src/constrains/SalesChannelMultiLanguageSelect/slice/index.ts
 */
import {
  createSlice,
  createSelector,
  createAsyncThunk,
} from '@reduxjs/toolkit';
import {
  getLanguageList,
  getSpecificLanguageList,
} from 'src/request/salesChannel/multiLanguage';
import { EnumReducers } from 'src/store/constants';

const target = (state) => {
  return state[EnumReducers.salesChannelLanguageSelect];
};

export const fetchSalesChannelAvailableLanguage = createAsyncThunk(
  'fetchSalesChannelAvailableLanguage',
  async (data: { salesChannelUuid: string }) => {
    const { salesChannelUuid } = data;

    const response = await getLanguageList(salesChannelUuid);
    return { response, salesChannelUuid };
  },
);

export const fetchSalesChannelSpecificLanguageList = createAsyncThunk(
  'fetchSalesChannelSpecificLanguageList',
  async (data: any) => {
    const { salesChannelUuid, body, uuid, reduxAccessKeys } = data;

    const response = await getSpecificLanguageList(body);
    return { response, salesChannelUuid, uuid, reduxAccessKeys };
  },
);

const getSourceLanguageFromList = (languageList, tag) => {
  if (!languageList || !tag) return null;
  const targetLanguage = languageList.find((item) => item.tag === tag);
  return targetLanguage?.translationSetting?.sourceLanguage || null;
};

const defaultData = {
  salesChannelUuid: null,
  selectedLanguage: null,
  languageList: null,
  pages: {},
  primaryLanguage: null,
  sourceLanguage: null,
};

const salesChannelContentAppearance = createSlice({
  name: 'salesChannelMultiLanguageSelect',
  initialState: defaultData,
  reducers: {
    updateLanguageList(state, action) {
      console.log(action);
    },
    updateSelectedLanguage(state, action) {
      const { salesChannelUuid, selectedLanguage } = action.payload;
      if (state.salesChannelUuid === salesChannelUuid) {
        state.selectedLanguage = selectedLanguage;
        state.sourceLanguage = getSourceLanguageFromList(
          state.languageList,
          selectedLanguage,
        );
      }
    },
    updateToPrimaryLanguage(state, action) {
      const { salesChannelUuid } = action.payload;
      if (state.salesChannelUuid === salesChannelUuid) {
        state.selectedLanguage = state.primaryLanguage;
        state.sourceLanguage = getSourceLanguageFromList(
          state.languageList,
          state.primaryLanguage,
        );
      }
    },
    resetLanguageState(state, action) {
      const { salesChannelUuid, languageList, primaryLanguage } =
        action.payload || {};

      state.salesChannelUuid = salesChannelUuid;
      if (languageList) state.languageList = languageList;
      const derivedPrimaryLanguage =
        primaryLanguage ||
        (languageList || []).find((i) => i.primary)?.tag ||
        null;
      if (derivedPrimaryLanguage) {
        state.primaryLanguage = derivedPrimaryLanguage;
        state.selectedLanguage = derivedPrimaryLanguage;
      }
      state.pages = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchSalesChannelAvailableLanguage.fulfilled,
      (state, action) => {
        const { response, salesChannelUuid } = action.payload;

        if (state.salesChannelUuid !== salesChannelUuid) {
          state.salesChannelUuid = salesChannelUuid;
          const primaryLanguage = (response.find((i) => i.primary) || {}).tag;
          state.primaryLanguage = primaryLanguage;
          state.selectedLanguage = primaryLanguage;
          state.sourceLanguage = getSourceLanguageFromList(
            response,
            primaryLanguage,
          );
          state.pages = {};
          state.languageList = response;
        }
      },
    );
    builder.addCase(
      fetchSalesChannelSpecificLanguageList.fulfilled,
      (state, action) => {
        const { uuid, response, reduxAccessKeys, salesChannelUuid } =
          action.payload;

        if (state.salesChannelUuid !== salesChannelUuid) {
          state.salesChannelUuid = salesChannelUuid;
          const primaryLanguage = (response.find((i) => i.primary) || {}).tag;
          state.primaryLanguage = primaryLanguage;
          state.selectedLanguage = primaryLanguage;
          state.sourceLanguage = getSourceLanguageFromList(
            response,
            primaryLanguage,
          );
          state.pages = {};
          state.languageList = response;
        }

        reduxAccessKeys.forEach((i) => {
          state.pages[`${uuid}${i}`] = response;
        });
      },
    );
  },
});

const currentSalesChannelUuid = () => {
  return createSelector(
    (state: any) => {
      return target(state).salesChannelUuid;
    },
    (salesChannelUuid) => {
      return salesChannelUuid;
    },
  );
};

const currentSelectedLanguage = () => {
  return createSelector(
    (state: any) => {
      return target(state).selectedLanguage;
    },
    (selectedLanguage) => {
      return selectedLanguage;
    },
  );
};

const currentPrimaryLanguage = () => {
  return createSelector(
    (state: any) => {
      return target(state).primaryLanguage;
    },
    (primaryLanguage) => {
      return primaryLanguage;
    },
  );
};

const currentSourceLanguage = () => {
  return createSelector(
    (state: any) => target(state).sourceLanguage,
    (sourceLanguage) => sourceLanguage,
  );
};

const isPrimaryLanguage = () => {
  return createSelector(
    (state: any) => {
      return target(state).primaryLanguage;
    },
    (state: any) => {
      return target(state).selectedLanguage;
    },
    (primaryLanguage, selectedLanguage) => {
      return primaryLanguage === selectedLanguage;
    },
  );
};

const getPageLanguageList = (key) => {
  return createSelector(
    (state: any) => {
      return target(state).pages[key];
    },
    (pages) => {
      return pages;
    },
  );
};

const getCurrentLanguageList = (uuid) => {
  return createSelector(
    (state: any) => {
      return target(state).languageList;
    },
    (languageList) => {
      return languageList;
    },
  );
};

export {
  currentSalesChannelUuid,
  getCurrentLanguageList,
  currentSelectedLanguage,
  getPageLanguageList,
  currentPrimaryLanguage,
  currentSourceLanguage,
  isPrimaryLanguage,
};
export const {
  updateLanguageList,
  updateSelectedLanguage,
  updateToPrimaryLanguage,
  resetLanguageState,
  //   resetData,
  //   onButtonTextChange,
  //   onCollapseOpen,
  //   onEmptyStateSetting,
  //   onErrorChange,
  //   onFeaturedProductChange,
  //   validation,
  //   onNoticeBoardChange,
} = salesChannelContentAppearance.actions;

export default salesChannelContentAppearance.reducer;
